import type { TEmailBuilderReferencedMediaMap } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import { isGroup } from "../domain/sections";
import type {
  Block,
  EmailDocument,
  ImageBlock,
  MediaRef,
  SocialBlock,
} from "../domain/types";
import { imageBlockPatchForMediaChange } from "./imageBlockMediaChange";

const DEFAULT_CONTENT_MAX_WIDTH = 600;

function isUnavailableLibraryMediaRef(
  ref: MediaRef,
  referencedMedia: TEmailBuilderReferencedMediaMap,
): boolean {
  if (ref.kind !== "library") {
    return false;
  }

  const mediaId = ref.mediaId.trim();
  if (mediaId.length === 0) {
    return false;
  }

  return referencedMedia[mediaId] === undefined;
}

function repairImageBlock(
  block: ImageBlock,
  referencedMedia: TEmailBuilderReferencedMediaMap,
): { block: ImageBlock; repairedCount: number } {
  const media = block.media;
  if (media === null || !isUnavailableLibraryMediaRef(media, referencedMedia)) {
    return { block, repairedCount: 0 };
  }

  return {
    block: {
      ...block,
      ...imageBlockPatchForMediaChange({
        block,
        action: "remove",
        contentMaxWidth: DEFAULT_CONTENT_MAX_WIDTH,
      }),
    },
    repairedCount: 1,
  };
}

function repairSocialBlock(
  block: SocialBlock,
  referencedMedia: TEmailBuilderReferencedMediaMap,
): { block: SocialBlock; repairedCount: number } {
  let repairedCount = 0;

  const links = block.links.map(link => {
    const icon = link.icon;
    if (icon === null || icon === undefined || !isUnavailableLibraryMediaRef(icon, referencedMedia)) {
      return link;
    }

    repairedCount += 1;
    return { ...link, icon: null };
  });

  if (repairedCount === 0) {
    return { block, repairedCount: 0 };
  }

  return {
    block: { ...block, links },
    repairedCount,
  };
}

function repairBlock(
  block: Block,
  referencedMedia: TEmailBuilderReferencedMediaMap,
): { block: Block; repairedCount: number } {
  if (block.type === "image") {
    return repairImageBlock(block, referencedMedia);
  }

  if (block.type === "social") {
    return repairSocialBlock(block, referencedMedia);
  }

  return { block, repairedCount: 0 };
}

export function repairEmailDocumentUnavailableLibraryMedia(
  document: EmailDocument,
  referencedMedia: TEmailBuilderReferencedMediaMap,
): { document: EmailDocument; repairedCount: number } {
  let repairedCount = 0;

  const sections = document.sections.map(section => {
    if (isGroup(section)) {
      const rows = section.rows.map(row => ({
        ...row,
        columns: row.columns.map(column => ({
          ...column,
          blocks: column.blocks.map(block => {
            const result = repairBlock(block, referencedMedia);
            repairedCount += result.repairedCount;
            return result.block;
          }),
        })),
      }));

      return { ...section, rows };
    }

    const columns = section.columns.map(column => ({
      ...column,
      blocks: column.blocks.map(block => {
        const result = repairBlock(block, referencedMedia);
        repairedCount += result.repairedCount;
        return result.block;
      }),
    }));

    return { ...section, columns };
  });

  return {
    document: { ...document, sections },
    repairedCount,
  };
}
